import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  CalendarCheck,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  Loader2,
} from "lucide-react";
import { GhostCalendar } from "@/components/Ghost";
import { FadeUp, Eyebrow } from "@/lib/motion";
import { loadHighLevelEmbed } from "@/lib/highlevel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EXPECT = [
  "Direct time with Todd — not a sales rep",
  "A plain-language read on your website and rankings",
  "An honest yes-or-no on whether we can help",
];

function ToddPhoto() {
  return (
    <>
      <div className="absolute -left-4 -top-4 h-full w-full rounded-[28px] border border-slate-200" />
      <img
        src="/img/todd-leather.webp"
        alt="Todd Meisler, owner and strategist at ZD Design"
        loading="lazy"
        className="relative h-[380px] w-full rounded-[28px] border border-slate-200 object-cover object-top shadow-lg"
      />
      <div className="absolute bottom-5 left-5 rounded-xl bg-white/95 px-5 py-3 shadow-md">
        <div className="font-display text-lg font-bold text-slate-900">
          Todd Meisler
        </div>
        <div className="font-mono text-[13px] uppercase tracking-[0.18em] text-flame">
          Founder &amp; Lead Strategist
        </div>
      </div>
    </>
  );
}

export default function Booking({ highLevel = false }) {
  const [days, setDays] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [selDate, setSelDate] = useState(null);
  const [selTime, setSelTime] = useState(null);
  const [form, setForm] = useState({ name: "", email: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (highLevel) loadHighLevelEmbed();
  }, [highLevel]);

  // HighLevel calendar: when an appointment is booked, send them to the thank-you page.
  useEffect(() => {
    if (!highLevel) return;
    const onMessage = (e) => {
      if (!e.origin || !e.origin.includes("leadconnectorhq.com")) return;
      const payload = JSON.stringify(e.data || "").toLowerCase();
      if (
        payload.includes("appointment") ||
        payload.includes("booked") ||
        payload.includes("scheduled")
      ) {
        navigate("/thank-you");
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [highLevel, navigate]);

  // After a booking lands, show the confirmation beat then hand off to /thank-you.
  useEffect(() => {
    if (done && !highLevel) {
      const t = setTimeout(() => navigate("/thank-you"), 1600);
      return () => clearTimeout(t);
    }
  }, [done, highLevel, navigate]);

  const usTime = (t) => {
    const [h, m] = t.split(":").map(Number);
    const hr = h % 12 === 0 ? 12 : h % 12;
    return `${hr}:${String(m).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;
  };

  const load = async () => {
    try {
      const res = await axios.get(`${API}/booking/slots`);
      setDays(res.data.days);
      setSelDate(
        (prev) =>
          prev ||
          (res.data.days.find((d) => d.slots.some((s) => s.available)) ||
            res.data.days[0]
          ).date
      );
      setLoadError(false);
    } catch (e) {
      setLoadError(true);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const activeDay = days ? days.find((d) => d.date === selDate) || days[0] : null;

  const [monthIdx, setMonthIdx] = useState(0);
  const months = useMemo(() => {
    if (!days) return [];
    const byDate = Object.fromEntries(days.map((d) => [d.date, d]));
    const build = (offset) => {
      const now = new Date();
      const first = new Date(now.getFullYear(), now.getMonth() + offset, 1);
      const y = first.getFullYear();
      const m = first.getMonth();
      const label = first.toLocaleString("en-US", { month: "long", year: "numeric" });
      const weeks = [];
      let cell = new Date(y, m, 1);
      let week = new Array(cell.getDay()).fill(null);
      while (cell.getMonth() === m) {
        const iso = `${cell.getFullYear()}-${String(cell.getMonth() + 1).padStart(2, "0")}-${String(cell.getDate()).padStart(2, "0")}`;
        week.push({ iso, day: cell.getDate(), data: byDate[iso] || null });
        if (week.length === 7) {
          weeks.push(week);
          week = [];
        }
        cell = new Date(cell.getFullYear(), cell.getMonth(), cell.getDate() + 1);
      }
      while (week.length && week.length < 7) week.push(null);
      if (week.length) weeks.push(week);
      return { label, weeks };
    };
    return [build(0), build(1)];
  }, [days]);
  const month = months[Math.min(monthIdx, months.length - 1)];

  const book = async (e) => {
    e.preventDefault();
    if (submitting || !selTime) return;
    const label = `${activeDay.weekday}, ${activeDay.month} ${activeDay.day} · ${usTime(selTime)} CT`;
    setSubmitting(true);
    try {
      await axios.post(`${API}/booking`, {
        name: form.name,
        email: form.email,
        date: selDate,
        time: selTime,
      });
      setDone({ label });
      toast.success("Call booked — Todd follows up personally to confirm every slot.");
      setSelTime(null);
      setForm({ name: "", email: "" });
      load();
    } catch (err) {
      if (err.response && err.response.status === 409) {
        toast.error("That slot was just taken — pick another time.");
        setSelTime(null);
        load();
      } else {
        toast.error("Booking failed. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="book"
      data-testid="booking-section"
      className="relative overflow-hidden border-t border-slate-200 bg-gradient-to-b from-white via-[#F8F9FA] to-[#F1F3F5] py-24 md:py-32"
    >
      <div className="pointer-events-none absolute -left-40 bottom-0 h-[420px] w-[420px] rounded-full bg-flame/10 blur-[140px]" />
      <GhostCalendar className="-right-16 -top-16 text-slate-900/[0.06]" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-2 lg:gap-20">
          <div>
            <FadeUp>
              <Eyebrow>Free strategy call</Eyebrow>
              <h2 className="mt-5 font-display text-4xl font-bold leading-[1.02] tracking-tight text-slate-900 sm:text-5xl">
                Pick a time. <span className="text-flame">Talk to Todd.</span>
              </h2>
              <p className="mt-6 max-w-md text-lg leading-relaxed text-mist md:text-xl">
                Choose a slot that works for you and the calendar reserves it
                on the spot — Todd follows up personally to confirm.
              </p>
            </FadeUp>
            <FadeUp delay={0.15}>
              <ul className="mt-10 space-y-4">
                {EXPECT.map((p) => (
                  <li
                    key={p}
                    className="flex items-center gap-3 text-lg text-slate-800"
                  >
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-flame" />
                    {p}
                  </li>
                ))}
              </ul>
            </FadeUp>
            <FadeUp delay={0.2}>
              <div
                data-testid="booking-photo"
                className="relative mt-10 hidden max-w-md lg:block"
              >
                <ToddPhoto />
              </div>
            </FadeUp>
          </div>

          {highLevel ? (
            <FadeUp delay={0.1}>
              <div
                data-testid="booking-highlevel-widget"
                className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_24px_60px_-30px_rgba(15,23,42,0.25)]"
              >
                <iframe
                  src="https://api.leadconnectorhq.com/widget/booking/qEpJHj1iR0xChPgnl76v"
                  title="Book a free strategy call with Todd Meisler"
                  allow="payment"
                  scrolling="no"
                  id="qEpJHj1iR0xChPgnl76v_1793137666169"
                  data-testid="booking-highlevel-iframe"
                  style={{ width: "100%", height: 620, border: "none", overflow: "hidden" }}
                />
              </div>
            </FadeUp>
          ) : (
            <FadeUp delay={0.1}>
            <div
              data-testid="booking-widget"
              className="glass min-w-0 rounded-2xl border border-slate-200 p-7 md:p-9"
            >
              {done ? (
                <div
                  data-testid="booking-success"
                  className="flex flex-col items-center py-8 text-center"
                >
                  <span className="flex h-16 w-16 items-center justify-center rounded-full border border-flame/40 bg-flame/10">
                    <CalendarCheck className="h-7 w-7 text-flame" />
                  </span>
                  <h3 className="mt-6 font-display text-2xl font-bold text-slate-900">
                    You're booked.
                  </h3>
                  <p className="mt-3 font-mono text-base uppercase tracking-[0.2em] text-flame">
                    {done.label}
                  </p>
                  <p className="mt-4 max-w-xs text-lg leading-relaxed text-mist">
                    Your call with Todd is reserved. He follows up personally
                    to confirm — no call centers, no calendar links.
                  </p>
                  <button
                    data-testid="booking-reset-button"
                    onClick={() => setDone(null)}
                    className="mt-8 rounded-2xl border border-slate-300 px-6 py-3 font-mono text-base font-semibold uppercase tracking-[0.15em] text-slate-900 transition-colors hover:border-flame/60 hover:text-flame"
                  >
                    Book another time
                  </button>
                </div>
              ) : loadError ? (
                <div
                  data-testid="booking-error"
                  className="py-8 text-center text-base text-mist"
                >
                  Couldn't load the calendar right now. Please try again
                  shortly, or send a message through the form below.
                </div>
              ) : !days ? (
                <div
                  data-testid="booking-loading"
                  className="flex items-center justify-center gap-3 py-12 text-base text-faint"
                >
                  <Loader2 className="h-4 w-4 animate-spin text-flame" />
                  Loading Todd's calendar…
                </div>
              ) : (
                <form onSubmit={book} data-testid="booking-form">
                  <div className="grid gap-7 sm:grid-cols-[1.15fr_1fr]">
                    <div>
                      <div className="font-mono text-base uppercase tracking-[0.22em] text-slate-800">
                        1 · Pick a day
                      </div>
                      <div className="mt-3 flex items-center justify-between">
                        <button
                          type="button"
                          data-testid="booking-month-prev"
                          aria-label="Previous month"
                          onClick={() => setMonthIdx((m) => Math.max(0, m - 1))}
                          disabled={monthIdx === 0}
                          className="rounded-lg border border-slate-200 p-1.5 text-slate-800 transition-colors hover:border-flame/60 hover:text-flame disabled:cursor-not-allowed disabled:opacity-30"
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </button>
                        <span className="font-display text-base font-bold text-slate-900">
                          {month.label}
                        </span>
                        <button
                          type="button"
                          data-testid="booking-month-next"
                          aria-label="Next month"
                          onClick={() => setMonthIdx((m) => Math.min(months.length - 1, m + 1))}
                          disabled={monthIdx >= months.length - 1}
                          className="rounded-lg border border-slate-200 p-1.5 text-slate-800 transition-colors hover:border-flame/60 hover:text-flame disabled:cursor-not-allowed disabled:opacity-30"
                        >
                          <ChevronRight className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="mt-3 grid grid-cols-7 gap-1 text-center font-mono text-[11px] uppercase tracking-[0.1em] text-faint">
                        {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((w) => (
                          <span key={w}>{w}</span>
                        ))}
                      </div>
                      <div className="mt-1 space-y-1">
                        {month.weeks.map((week, wi) => (
                          <div key={wi} className="grid grid-cols-7 gap-1">
                            {week.map((cell, ci) =>
                              !cell ? (
                                <span key={ci} />
                              ) : !cell.data ? (
                                <span
                                  key={ci}
                                  className="flex h-10 items-center justify-center rounded-lg font-mono text-base text-faint/50"
                                >
                                  {cell.day}
                                </span>
                              ) : (
                                <button
                                  key={ci}
                                  type="button"
                                  data-testid={`booking-day-${cell.iso}`}
                                  onClick={() => {
                                    setSelDate(cell.iso);
                                    setSelTime(null);
                                  }}
                                  className={`relative flex h-10 items-center justify-center rounded-lg border font-mono text-base transition-all duration-200 ${
                                    selDate === cell.iso
                                      ? "border-flame bg-flame font-semibold text-obsidian"
                                      : "border-slate-200 bg-white/60 text-slate-900 hover:border-flame/60 hover:text-flame"
                                  }`}
                                >
                                  {cell.day}
                                  {cell.data.slots.some((s) => s.available) && selDate !== cell.iso && (
                                    <span className="absolute bottom-1 h-1 w-1 rounded-full bg-flame" />
                                  )}
                                </button>
                              )
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="border-t border-slate-200 pt-5 sm:border-l sm:border-t-0 sm:pl-7 sm:pt-0">
                      <div className="flex items-center gap-2 font-mono text-base uppercase tracking-[0.22em] text-slate-800">
                        2 · Pick a time
                        <Clock className="h-3 w-3" />
                      </div>
                      {activeDay && (
                        <div className="mt-2 font-mono text-[13px] uppercase tracking-[0.15em] text-faint">
                          {activeDay.weekday}, {activeDay.month} {activeDay.day}
                        </div>
                      )}
                      <div className="mt-3 grid grid-cols-2 gap-2">
                        {activeDay.slots.map((s) => (
                          <button
                            key={activeDay.date + s.time}
                            type="button"
                            data-testid={`booking-slot-${activeDay.date}-${s.time.replace(":", "")}`}
                            onClick={() => setSelTime(s.available ? s.time : null)}
                            disabled={!s.available}
                            className={`rounded-lg border px-2 py-3 font-mono text-base transition-all duration-300 ${
                              selTime === s.time
                                ? "border-flame bg-flame text-obsidian"
                                : s.available
                                ? "border-slate-200 bg-white/60 text-slate-900 hover:border-flame/60 hover:text-flame"
                                : "cursor-not-allowed border-slate-100 bg-white/30 text-faint line-through opacity-50"
                            }`}
                          >
                            {usTime(s.time)}
                          </button>
                        ))}
                      </div>
                      <div className="mt-3 font-mono text-[13px] uppercase tracking-[0.18em] text-faint">
                        All times Central · Mon–Fri only
                      </div>
                    </div>
                  </div>

                  {selTime && (
                    <div className="mt-6 border-t border-slate-200 pt-6">
                      <div className="font-mono text-base uppercase tracking-[0.22em] text-slate-800">
                        3 · Your details
                      </div>
                      <div className="mt-3 space-y-4">
                        <input
                          data-testid="booking-name-input"
                          type="text"
                          required
                          value={form.name}
                          onChange={(e) =>
                            setForm({ ...form, name: e.target.value })
                          }
                          placeholder="Your name"
                          className="w-full rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-lg text-slate-900 placeholder:text-slate-700 outline-none transition-colors focus:border-flame"
                        />
                        <input
                          data-testid="booking-email-input"
                          type="email"
                          required
                          value={form.email}
                          onChange={(e) =>
                            setForm({ ...form, email: e.target.value })
                          }
                          placeholder="you@yourbusiness.com"
                          className="w-full rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-lg text-slate-900 placeholder:text-slate-700 outline-none transition-colors focus:border-flame"
                        />
                        <button
                          data-testid="booking-confirm-button"
                          type="submit"
                          disabled={submitting}
                          className="w-full rounded-2xl bg-flame px-7 py-4 font-mono text-base font-semibold uppercase tracking-[0.15em] text-obsidian transition-all duration-300 hover:shadow-[0_0_40px_rgba(255,85,0,0.5)] disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {submitting
                            ? "Reserving…"
                            : `Book ${activeDay.weekday} ${activeDay.day} ${activeDay.month} · ${usTime(selTime)} CT`}
                        </button>
                      </div>
                    </div>
                  )}
                </form>
              )}
            </div>
            </FadeUp>
          )}
          {!highLevel ? (
            <FadeUp delay={0.1}>
              <div
                data-testid="booking-photo-mobile"
                className="relative mx-auto max-w-md lg:hidden"
              >
                <ToddPhoto />
              </div>
            </FadeUp>
          ) : null}
        </div>
      </div>
    </section>
  );
}
